import { motion } from "framer-motion";
import { 
  GripVertical, 
  Play, 
  CheckCircle2, 
  Video, 
  FileText, 
  Hash,
  Clock,
  Keyboard,
  Users,
} from "lucide-react";
import { Container, SectionHeading, SectionSubheading, FeatureCard } from "./shared";

function CourseBuilderSkeleton() {
  const lessons = [
    { title: "Introduction to the Course", type: "video", duration: "5:32", completed: true },
    { title: "Setting Up Your Environment", type: "video", duration: "12:45", completed: true },
    { title: "Core Concepts Deep Dive", type: "text", completed: false },
  ];

  return (
    <div className="absolute inset-0 p-4 flex flex-col gap-2">
      {lessons.map((lesson, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ delay: i * 0.1, duration: 0.4 }}
          viewport={{ once: true }}
          className="bg-white dark:bg-neutral-800 rounded-lg p-3 border border-neutral-200 dark:border-neutral-700 shadow-sm flex items-center gap-3"
        >
          <GripVertical className="h-4 w-4 text-neutral-400 flex-shrink-0" />
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${lesson.completed ? 'bg-emerald-100 dark:bg-emerald-900/30' : 'bg-neutral-100 dark:bg-neutral-700'}`}>
            {lesson.type === "video" ? (
              <Video className={`h-4 w-4 ${lesson.completed ? 'text-emerald-600 dark:text-emerald-400' : 'text-neutral-500'}`} />
            ) : (
              <FileText className={`h-4 w-4 ${lesson.completed ? 'text-emerald-600 dark:text-emerald-400' : 'text-neutral-500'}`} />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-neutral-800 dark:text-neutral-200 truncate">{lesson.title}</div>
            {lesson.duration && (
              <div className="text-xs text-neutral-500">{lesson.duration}</div>
            )}
          </div>
          {lesson.completed && (
            <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0" />
          )}
        </motion.div>
      ))}
      
      {/* Add Lesson Button */}
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ delay: 0.4, duration: 0.4 }}
        viewport={{ once: true }}
        className="border-2 border-dashed border-neutral-300 dark:border-neutral-600 rounded-lg p-3 flex items-center justify-center text-neutral-500 text-sm"
      >
        + Add Lesson
      </motion.div>
    </div>
  );
}

// Skeleton 2: Video Player
function VideoPlayerSkeleton() {
  const chapters = [
    { title: "Getting Started", time: "0:00", active: true },
    { title: "Core Concepts", time: "5:30", active: false },
    { title: "Advanced Topics", time: "12:15", active: false },
    { title: "Wrap Up", time: "18:45", active: false },
  ];

  return (
    <div className="absolute inset-0 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        whileInView={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        viewport={{ once: true }}
        className="bg-neutral-900 rounded-xl overflow-hidden h-full flex flex-col"
      >
        {/* Video Area */}
        <div className="relative flex-1 bg-neutral-800 flex items-center justify-center min-h-[120px]">
          <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center">
            <Play className="w-5 h-5 text-white ml-0.5" />
          </div>
          {/* Progress bar */}
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-neutral-700">
            <div className="h-full w-1/4 bg-emerald-500" />
          </div>
        </div>
        
        {/* Chapter List */}
        <div className="p-2 bg-neutral-900 space-y-1">
          {chapters.map((chapter, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: 10 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 + i * 0.1, duration: 0.3 }}
              viewport={{ once: true }}
              className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-xs ${
                chapter.active 
                  ? 'bg-emerald-500/20 text-emerald-400' 
                  : 'text-neutral-400 hover:bg-neutral-800'
              }`}
            >
              <Clock className="h-3 w-3" />
              <span className="flex-1 truncate">{chapter.title}</span>
              <span className="text-[10px] opacity-70">{chapter.time}</span>
            </motion.div>
          ))}
        </div>
        
        {/* Keyboard hint */}
        <div className="px-2 pb-2 flex items-center gap-1 text-[10px] text-neutral-500">
          <Keyboard className="h-3 w-3" />
          <span>Press Space to play</span>
        </div>
      </motion.div>
    </div>
  );
}

// Skeleton 3: Community
function CommunitySkeleton() {
  const channels = [
    { name: "general", unread: 3 },
    { name: "introductions", unread: 0 },
    { name: "q-and-a", unread: 12 },
  ];

  const messages = [
    { user: "Sarah", text: "Just finished module 3! 🎉", time: "2m" },
    { user: "Mike", text: "Can someone help with...", time: "5m" },
  ];

  return (
    <div className="absolute inset-0 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        whileInView={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        viewport={{ once: true }}
        className="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 overflow-hidden h-full flex"
      >
        {/* Sidebar */}
        <div className="w-1/3 border-r border-neutral-200 dark:border-neutral-700 p-2 space-y-1">
          {channels.map((channel, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -10 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 + i * 0.1, duration: 0.3 }}
              viewport={{ once: true }}
              className={`flex items-center gap-1.5 px-2 py-1.5 rounded-md text-xs ${
                i === 0 ? 'bg-neutral-100 dark:bg-neutral-700' : ''
              }`}
            >
              <Hash className="h-3 w-3 text-neutral-500" />
              <span className="flex-1 truncate text-neutral-700 dark:text-neutral-300">{channel.name}</span>
              {channel.unread > 0 && (
                <span className="bg-emerald-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">
                  {channel.unread}
                </span>
              )}
            </motion.div>
          ))}
        </div>
        
        {/* Messages */}
        <div className="flex-1 p-2 space-y-2">
          {messages.map((msg, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 + i * 0.1, duration: 0.3 }}
              viewport={{ once: true }}
              className="flex items-start gap-2"
            >
              <div className="w-6 h-6 rounded-full bg-neutral-200 dark:bg-neutral-600 flex items-center justify-center text-[10px] font-medium flex-shrink-0">
                {msg.user[0]}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-neutral-800 dark:text-neutral-200">{msg.user}</span>
                  <span className="text-[10px] text-neutral-400">{msg.time}</span>
                </div>
                <p className="text-xs text-neutral-600 dark:text-neutral-400 truncate">{msg.text}</p>
              </div>
            </motion.div>
          ))}
          
          {/* Online indicator */}
          <div className="flex items-center gap-1.5 text-[10px] text-neutral-500 mt-4">
            <Users className="h-3 w-3" />
            <span>12 members online</span>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export function FeaturesGrid() {
  return (
    <section id="features" className="py-16 md:py-24 lg:py-32 bg-neutral-100 dark:bg-neutral-950">
      <Container>
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-12 md:mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <SectionHeading className="text-center lg:text-left">
              Built for Creators <br className="hidden sm:block" />
              Who Deserve More.
            </SectionHeading>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            viewport={{ once: true }}
            className="lg:max-w-md"
          >
            <SectionSubheading className="text-center lg:text-left mx-auto lg:mx-0">
              Everything you need to create, sell, and grow — without the complexity or hidden fees.
            </SectionSubheading>
          </motion.div>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <FeatureCard
              title="Drag-and-Drop Course Builder"
              skeleton={<CourseBuilderSkeleton />}
              className="rounded-xl lg:rounded-l-3xl lg:rounded-r-xl h-full"
            />
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            viewport={{ once: true }}
          >
            <FeatureCard
              title="Custom Video Player with Chapters"
              skeleton={<VideoPlayerSkeleton />}
              className="rounded-xl h-full"
            />
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <FeatureCard
              title="Built-in Community Spaces"
              skeleton={<CommunitySkeleton />}
              className="rounded-xl lg:rounded-r-3xl lg:rounded-l-xl h-full"
            />
          </motion.div>
        </div>
      </Container>
    </section>
  );
}

